<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useEvents } from '@/composables/useEvents'
import EventForm from '@/components/organizer/events/EventForm.vue'
import type { StoreEventPayload } from '@/types/events'

interface EventFormData {
  name: string
  tagline: string
  description: string
  facebook_page: string
  instagram_page: string
  external_website_url: string
  mail_bcc: string
  contact_email: string
  maintenance: boolean
}

function isAxiosError(e: unknown): e is { response: { status: number; data: { errors?: Record<string, string[]> } } } {
  return (
    typeof e === 'object' &&
    e !== null &&
    'response' in e &&
    typeof (e as { response: unknown }).response === 'object' &&
    (e as { response: unknown }).response !== null
  )
}

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const { event, fetchEvent, updateEvent } = useEvents()

const eventSlug = route.params['eventSlug'] as string

const formLoading = ref(false)
const errors = ref<Record<string, string[]>>({})
const generalError = ref('')
const successMessage = ref<string | null>(null)

const form = ref<EventFormData>({
  name: '',
  tagline: '',
  description: '',
  facebook_page: '',
  instagram_page: '',
  external_website_url: '',
  mail_bcc: '',
  contact_email: '',
  maintenance: false,
})

onMounted(async () => {
  if (!event.value || event.value.slug !== eventSlug) {
    await fetchEvent(eventSlug)
  }
  if (event.value) {
    form.value = {
      name: event.value.name,
      tagline: event.value.tagline ?? '',
      description: event.value.description ?? '',
      facebook_page: event.value.facebook_page ?? '',
      instagram_page: event.value.instagram_page ?? '',
      external_website_url: event.value.external_website_url ?? '',
      mail_bcc: event.value.mail_bcc ?? '',
      contact_email: event.value.contact_email ?? '',
      maintenance: event.value.maintenance,
    }
  }
})

async function handleSubmit(data: StoreEventPayload): Promise<void> {
  errors.value = {}
  generalError.value = ''
  successMessage.value = null
  formLoading.value = true
  try {
    const updated = await updateEvent(eventSlug, data)
    form.value = { ...form.value, mail_bcc: updated.mail_bcc ?? '' }
    successMessage.value = t('events.settings.save_success')
    setTimeout(() => { successMessage.value = null }, 4000)
  } catch (e: unknown) {
    if (isAxiosError(e)) {
      if (e.response.status === 422 && e.response.data.errors) {
        errors.value = e.response.data.errors
      } else {
        generalError.value = t('events.form.error_generic')
      }
    } else {
      generalError.value = t('events.form.error_generic')
    }
  } finally {
    formLoading.value = false
  }
}
</script>

<template>
  <div class="bg-slate-800/50 border border-slate-700 rounded-2xl p-6 sm:p-8">
    <div
      v-if="generalError"
      class="mb-6 rounded-lg bg-red-500/10 border border-red-500/30 px-4 py-3 text-sm text-red-400"
    >
      {{ generalError }}
    </div>

    <div
      v-if="successMessage"
      class="mb-6 rounded-lg bg-emerald-500/10 border border-emerald-500/30 px-4 py-3 text-sm text-emerald-400"
    >
      {{ successMessage }}
    </div>

    <EventForm
      v-model="form"
      :loading="formLoading"
      :errors="errors"
      :is-edit="true"
      @submit="handleSubmit"
      @cancel="router.push(`/organizer/events/${eventSlug}`)"
    />
  </div>
</template>
